import React from 'react';
import PropTypes from 'prop-types';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';

import RichText from 'components/Core/RichText';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import Faq from 'components/Faq';
import PopularActivity from 'components/PopularActivity';
import CtaBlock from 'components/CtaBlock';
import LatestNews from 'components/LatestNews';
import Contact from 'components/Contact';
import Image from 'components/Core/Image';
import Cta from 'components/Core/Cta';
import Embed from 'components/Core/Embed';

import { ACTION_NODE_LINK } from 'constants/Action';

const getServiceCta = ({ link, linkTitle }) => {
  const linkBlock = link[0];
  const { type: linkType } = linkBlock;

  let urlHref;
  let label = linkTitle;

  const isExternalUrl = linkType === 'URLBlock';

  if (isExternalUrl) {
    const { value: url } = linkBlock;
    urlHref = url;
    label = linkTitle || url;
  } else if (linkBlock.page) {
    const {
      page: { url, title },
    } = linkBlock;
    urlHref = url;
    label = linkTitle || title;
  } else {
    urlHref = '/pagina-niet-gevonden';
    label = linkTitle;
  }

  return (
    <div className="my-4">
      {isExternalUrl && (
        <Cta
          className="w-full md:w-auto"
          node={ACTION_NODE_LINK}
          href={urlHref}
          target="_blank"
          rel="noopener noreferrer"
          showIcon={false}
        >
          {label}
        </Cta>
      )}
      {!isExternalUrl && (
        <Cta className="w-full md:w-auto" node={ACTION_NODE_LINK} href="/[...slug]" as={urlHref} showIcon={false}>
          {label}
        </Cta>
      )}
    </div>
  );
};

const SimpleContentPage = ({ hostname, page: { title, subtitle, body, breadcrumbs } }) => {
  return (
    <>
      <div className="container mx-auto px-4">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <h1 className="h2 lg:h1 py-4 md:pt-8 md:pb-4 text-secondary">{title}</h1>
        {subtitle && <h2 className="h3 lg:h2 py-4 md:pt-4 md:pb-8">{subtitle}</h2>}
      </div>

      <div className="container mx-auto px-4 u-block-margins">
        <div className="flex flex-wrap -mx-4">
          {/* Left content section */}
          <div className="flex-full max-w-full lg:flex-2/3 lg:max-w-2/3 px-4">
            {(body || []).map((block) => {
              if (block.blockType === 'ImageChooserBlock') {
                return (
                  <div className="mb-6 lg:mb-10" key={`block-${block.blockType}-${block.id}`}>
                    <Image image={block.img} />
                  </div>
                );
              }
              if (block.blockType === 'RichTextBlock') {
                return (
                  <RichText key={`block-${block.blockType}-${block.id}`} blockMargin={false} content={block.value} />
                );
              }
              if (block.blockType === 'NamedLinkBlock') {
                return <div key={`block-${block.blockType}-${block.id}`}>{getServiceCta(block)}</div>;
              }
              if (block.blockType === 'CustomEmbedBlock') {
                return (
                  <div key={`block-embed-${block.id}`} className="mb-6 lg:mb-10">
                    <Embed title={block.title} {...block.embed} />
                  </div>
                );
              }
              return <div key={`empty-${block.blockType}-${block.id}`} />;
            })}
          </div>
          {/* Right content section */}
          <div className="flex-full max-w-full lg:flex-1/3 lg:max-w-1/3 px-4" />
        </div>
      </div>
      {/* Bottom content section */}
      {(body || []).map((block) => {
        if (block.blockType === 'FAQBlock') {
          return <Faq key={`block-faq-${block.id}`} {...block} />;
        }
        if (block.blockType === 'PopularActivityBlock') {
          return <PopularActivity key={`block-popular-activity-${block.id}`} {...block} />;
        }
        if (block.blockType === 'CTABlock') {
          return <CtaBlock {...block} key={`block-cta-${block.id}`} />;
        }
        if (block.blockType === 'LatestNewsBlock') {
          return <LatestNews {...block} key={`block-latest-news-${block.id}`} />;
        }
        if (block.blockType === 'ContactBlock') {
          return <Contact hostname={hostname} {...block} key={`block-contact-form-${block.id}`} />;
        }
        return <div key={`empty-block-bottom-${block.id}`} />; // eslint-disable-line react/no-array-index-key
      })}
    </>
  );
};

SimpleContentPage.getCustomInitialProps = async ({ id, contentType, hostname, previewData }) => {
  const query = requireQueryByName('SimpleContentPage');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
    extraPageProps: {
      hostname,
    },
  };
};

SimpleContentPage.propTypes = {
  hostname: PropTypes.string.isRequired,
  page: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    url: PropTypes.string.isRequired,
    body: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ),
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};

export default SimpleContentPage;
